/**
 * Shared native window-chrome constants for the Studio desktop shell. They live
 * in the bridge package so the Electron main process, the preload theme sync,
 * and the Studio renderer all read a single source of truth: a mismatch between
 * any two of them makes the titlebar height jump or the colors flash on the
 * first theme sync.
 */

/** Native titlebar height (px); the renderer mirrors this via `--top-app-bar-height`. */
export const TITLEBAR_HEIGHT = 80

/** CSS custom property the renderer reads for the (taller, on desktop) header. */
export const TOP_APP_BAR_HEIGHT_CSS_VAR = '--top-app-bar-height'

/**
 * Id of the Studio header element the desktop shell drives as the native
 * titlebar. The preload sync samples its computed colors; main sizes the
 * window-controls overlay to match.
 */
export const TITLEBAR_ELEMENT_ID = 'studio-titlebar'

/**
 * Top-left position of the macOS traffic lights. Placed near the top so they
 * sit above the Studio logo (which stays at the far left) instead of beside it.
 */
export const TRAFFIC_LIGHT_POSITION = { x: 13, y: 10 } as const

/**
 * Whether the desktop shell hides the native titlebar and paints the Studio
 * header as its own draggable titlebar (with native traffic lights on top).
 * Only macOS does this; Windows/Linux keep the native frame, title bar, and
 * menu so window controls and "Check for Updates" stay in their conventional
 * place. Pass a Node `process.platform` string; the rule lives here so main,
 * preload, and the renderer all agree.
 */
export function usesCustomTitlebar(platform: string): boolean {
  return platform === 'darwin'
}

/**
 * Dark Studio chrome fallback shown during load gaps before the renderer mounts
 * and the preload sync pushes the actual computed theme colors.
 */
export const FALLBACK_BACKGROUND_COLOR = '#1a1c1f'
export const FALLBACK_SYMBOL_COLOR = '#f9fafb'
